'use client'

import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '@/store/hooks'
import {
    getConsultation,
    updateConsultation,
    updateConsultationStatus,
    uploadConsultationAudio,
    transcribeConsultation,
    analyzeConsultation,
    Consultation,
    CONSULTATION_STATUS_LABELS,
} from '@/lib/api/consultations/consultation-api'
import { toast } from 'react-toastify'
import { useNavigate, useParams } from 'react-router-dom'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
    MdArrowBack,
    MdMedicalServices,
    MdPerson,
    MdCalendarToday,
    MdSave,
    MdCheck,
    MdMedication,
    MdDescription,
    MdAutoAwesome,
} from 'react-icons/md'
import { AudioRecorder } from '@/components/audio-recorder'
import { TranscriptionViewer } from '@/components/consultation/transcription-viewer'
import { AiSummaryCard } from '@/components/consultation/ai-summary-card'
import ConfirmModal from '@/components/confirm-modal'
import { ExamRequestButton } from '@/components/medical/exam-request-modal'

export default function ConsultationRoomPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const [consultation, setConsultation] = useState<Consultation | null>(null)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [uploadingAudio, setUploadingAudio] = useState(false)
    const [transcribing, setTranscribing] = useState(false)
    const [analyzing, setAnalyzing] = useState(false)
    const [notes, setNotes] = useState('')
    const [hasRecording, setHasRecording] = useState(false)
    const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null)
    const [recordedDuration, setRecordedDuration] = useState(0)
    const [realTimeTranscription, setRealTimeTranscription] = useState('')
    const [isTranscribing, setIsTranscribing] = useState(false)
    const [showFinishModal, setShowFinishModal] = useState(false)

    useEffect(() => {
        if (id) {
            loadConsultation()
        }
    }, [id])

    const loadConsultation = async () => {
        if (!id) return

        setLoading(true)
        try {
            const response = await getConsultation(parseInt(id, 10))
            if (response.success && response.result) {
                setConsultation(response.result)
                setNotes(response.result.notes || '')

                // Start consultation if not already
                if (
                    response.result.status === 'SCHEDULED' ||
                    response.result.status === 'WAITING'
                ) {
                    await updateConsultationStatus(parseInt(id, 10), 'IN_PROGRESS')
                    setConsultation(prev =>
                        prev ? { ...prev, status: 'IN_PROGRESS' } : prev
                    )
                }
            } else {
                toast.error(response.error || 'Erro ao carregar consulta')
                navigate('/bookings/consultations')
            }
        } catch (error) {
            toast.error('Erro ao carregar consulta')
            navigate('/bookings/consultations')
        } finally {
            setLoading(false)
        }
    }

    const handleRecordingComplete = useCallback(
        async (audioBlob: Blob, duration: number) => {
            setRecordedBlob(audioBlob)
            setRecordedDuration(duration)
            setHasRecording(true)
            toast.success(`Gravação finalizada! Duração: ${Math.floor(duration / 60)}min ${duration % 60}s`)
        },
        []
    )

    const handleRecordingError = useCallback((error: string) => {
        toast.error(error)
    }, [])

    const handleTranscriptionUpdate = useCallback(
        (text: string, isFinal: boolean) => {
            setRealTimeTranscription(text)
            setIsTranscribing(!isFinal)
        },
        []
    )

    const handleSaveNotes = async () => {
        if (!id) return

        setSaving(true)
        try {
            const response = await updateConsultation(parseInt(id, 10), { notes })
            if (response.success) {
                toast.success('Anotacoes salvas!')
            } else {
                toast.error(response.error || 'Erro ao salvar anotações')
            }
        } catch (error) {
            toast.error('Erro ao salvar anotações')
        } finally {
            setSaving(false)
        }
    }

    const handleUploadAudio = async () => {
        if (!id || !recordedBlob) return

        setUploadingAudio(true)
        try {
            const response = await uploadConsultationAudio(
                parseInt(id, 10),
                recordedBlob
            )
            if (response.success && response.result) {
                let updatedConsultation = response.result

                // Save real-time transcription if available
                if (realTimeTranscription.trim()) {
                    const transcriptionResponse = await updateConsultation(
                        parseInt(id, 10),
                        {
                            transcription: realTimeTranscription.trim(),
                            transcriptionStatus: 'COMPLETED',
                        }
                    )
                    if (transcriptionResponse.success && transcriptionResponse.result) {
                        updatedConsultation = transcriptionResponse.result
                        toast.success('Audio e transcrição salvos com sucesso!')
                    } else {
                        toast.success('Audio enviado! Transcrição sera processada.')
                    }
                } else {
                    toast.success('Audio enviado com sucesso!')
                }

                setConsultation(updatedConsultation)
                setRecordedBlob(null)
                setHasRecording(false)
                setRealTimeTranscription('')
            } else {
                toast.error(response.error || 'Erro ao enviar audio')
            }
        } catch (error) {
            toast.error('Erro ao enviar audio')
        } finally {
            setUploadingAudio(false)
        }
    }

    const handleTranscribe = async () => {
        if (!id) return

        setTranscribing(true)
        try {
            const response = await transcribeConsultation(parseInt(id, 10))
            if (response.success) {
                toast.success('Transcrição em andamento...')
                // Poll for completion
                const pollInterval = setInterval(async () => {
                    const updated = await getConsultation(parseInt(id, 10))
                    if (updated.success && updated.result) {
                        setConsultation(updated.result)
                        if (
                            updated.result.transcriptionStatus === 'COMPLETED' ||
                            updated.result.transcriptionStatus === 'FAILED'
                        ) {
                            clearInterval(pollInterval)
                            setTranscribing(false)
                            if (updated.result.transcriptionStatus === 'COMPLETED') {
                                toast.success('Transcrição concluída!')
                            }
                        }
                    }
                }, 3000)
            } else {
                toast.error(response.error || 'Erro ao iniciar transcrição')
                setTranscribing(false)
            }
        } catch (error) {
            toast.error('Erro ao iniciar transcrição')
            setTranscribing(false)
        }
    }

    const handleAnalyze = async () => {
        if (!id) return

        setAnalyzing(true)
        try {
            const response = await analyzeConsultation(parseInt(id, 10))
            if (response.success) {
                toast.success('Análise IA concluída!')
                loadConsultation()
            } else {
                toast.error(response.error || 'Erro ao gerar análise')
            }
        } catch (error) {
            toast.error('Erro ao gerar análise')
        } finally {
            setAnalyzing(false)
        }
    }

    const handleFinishConsultation = () => {
        setShowFinishModal(true)
    }

    const confirmFinishConsultation = async () => {
        if (!id) return

        try {
            // Save notes and transcription first
            const updateData: any = {}
            if (notes) {
                updateData.notes = notes
            }
            if (realTimeTranscription.trim() && !consultation?.transcription) {
                updateData.transcription = realTimeTranscription.trim()
                updateData.transcriptionStatus = 'COMPLETED'
            }

            if (Object.keys(updateData).length > 0) {
                await updateConsultation(parseInt(id, 10), updateData)
            }

            // Upload audio if exists
            if (recordedBlob) {
                await uploadConsultationAudio(parseInt(id, 10), recordedBlob)
            }

            // Update status
            const response = await updateConsultationStatus(
                parseInt(id, 10),
                'COMPLETED'
            )
            if (response.success) {
                toast.success('Consulta concluida!')
                navigate(`/bookings/consultations/${id}`)
            } else {
                toast.error(response.error || 'Erro ao finalizar consulta')
            }
        } catch (error) {
            toast.error('Erro ao finalizar consulta')
        } finally {
            setShowFinishModal(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando sala de consulta...
                </p>
            </div>
        )
    }

    if (!consultation) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                    <MdMedicalServices className="text-4xl text-base-content/30" />
                </div>
                <div className="text-center">
                    <h3 className="text-lg font-semibold text-base-content mb-1">
                        Consulta nao encontrada
                    </h3>
                </div>
                <button
                    onClick={() => navigate('/bookings/consultations')}
                    className="btn btn-secondary"
                >
                    Voltar para Consultas
                </button>
            </div>
        )
    }

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 border-b border-base-300/50 px-4 py-3">
                <div className="flex items-center justify-between max-w-7xl mx-auto">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(`/bookings/consultations/${consultation.id}`)}
                            className="btn btn-ghost btn-circle btn-sm"
                        >
                            <MdArrowBack className="text-xl" />
                        </button>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                                <MdMedicalServices className="text-2xl text-white" />
                            </div>
                            <div>
                                <h1 className="text-lg font-bold text-base-content">
                                    {consultation.title ||
                                        `Consulta #${consultation.id}`}
                                </h1>
                                <div className="flex items-center gap-2 text-sm text-base-content/60">
                                    <MdPerson className="text-sm" />
                                    <span>{consultation.client?.name}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="badge badge-secondary badge-lg animate-pulse">
                            {CONSULTATION_STATUS_LABELS[consultation.status]}
                        </span>
                        {/* Durante o atendimento é quando o exame é pedido — sem
                            preço, sem cobrança e sem sair da sala. */}
                        <ExamRequestButton
                            clientId={consultation.client?.id}
                            clientName={consultation.client?.name}
                        />
                        <button
                            onClick={handleFinishConsultation}
                            className="btn btn-success gap-2"
                        >
                            <MdCheck className="text-lg" />
                            Finalizar Consulta
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 overflow-auto p-4">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - Patient Info & Notes */}
                    <div className="flex flex-col gap-4">
                        {/* Patient Info */}
                        <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                            <div className="card-body p-4">
                                <h2 className="card-title text-base mb-3">
                                    Dados do Paciente
                                </h2>
                                <div className="space-y-2 text-sm">
                                    <div className="flex items-center gap-2">
                                        <MdPerson className="text-secondary" />
                                        <span className="font-medium">
                                            {consultation.client?.name}
                                        </span>
                                    </div>
                                    {consultation.client?.email && (
                                        <p className="text-base-content/60 pl-6">
                                            {consultation.client.email}
                                        </p>
                                    )}
                                    <div className="flex items-center gap-2 text-base-content/60">
                                        <MdCalendarToday className="text-sm" />
                                        <span>
                                            {format(
                                                new Date(consultation.createdAt),
                                                "dd/MM/yyyy 'as' HH:mm",
                                                { locale: ptBR }
                                            )}
                                        </span>
                                    </div>
                                </div>

                                {consultation.triage && (
                                    <div className="mt-4 p-3 bg-warning/10 rounded-lg border border-warning/20">
                                        <h3 className="font-medium text-sm mb-2">
                                            Triagem
                                        </h3>
                                        <p className="text-xs">
                                            <strong>Urgência:</strong>{' '}
                                            {consultation.triage.urgency}
                                        </p>
                                        {consultation.triage.chiefComplaint && (
                                            <p className="text-xs mt-1">
                                                <strong>Queixa:</strong>{' '}
                                                {consultation.triage.chiefComplaint}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Notes */}
                        <div className="card bg-base-100 border border-base-300/50 shadow-sm flex-1">
                            <div className="card-body p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h2 className="card-title text-base">
                                        Anotações Rápidas
                                    </h2>
                                    <button
                                        onClick={handleSaveNotes}
                                        disabled={saving}
                                        className="btn btn-ghost btn-sm gap-1"
                                    >
                                        {saving ? (
                                            <span className="loading loading-spinner loading-xs"></span>
                                        ) : (
                                            <MdSave />
                                        )}
                                        Salvar
                                    </button>
                                </div>
                                <textarea
                                    className="textarea textarea-bordered w-full h-full min-h-[200px] text-sm"
                                    placeholder="Digite suas anotações durante a consulta..."
                                    value={notes}
                                    onChange={e => setNotes(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Center Column - Recording */}
                    <div className="flex flex-col gap-4">
                        {/* Audio Recorder */}
                        <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                            <div className="card-body p-4">
                                <h2 className="card-title text-base mb-3">
                                    Gravação da Consulta
                                </h2>

                                {!consultation.audioUrl ? (
                                    <>
                                        <AudioRecorder
                                            onRecordingComplete={handleRecordingComplete}
                                            onError={handleRecordingError}
                                            onTranscriptionUpdate={handleTranscriptionUpdate}
                                            enableRealTimeTranscription={true}
                                            transcriptionMode="whisper"
                                            consultationId={consultation.id}
                                            maxDuration={7200}
                                        />

                                        {hasRecording && recordedBlob && (
                                            <div className="mt-4 p-4 bg-success/10 rounded-lg border border-success/20">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="font-medium text-sm text-success">
                                                            Gravação concluída!
                                                        </p>
                                                        <p className="text-xs text-base-content/60">
                                                            Duração:{' '}
                                                            {Math.floor(
                                                                recordedDuration / 60
                                                            )}
                                                            min {recordedDuration % 60}s
                                                        </p>
                                                    </div>
                                                    <button
                                                        onClick={handleUploadAudio}
                                                        disabled={uploadingAudio}
                                                        className="btn btn-success btn-sm gap-2"
                                                    >
                                                        {uploadingAudio ? (
                                                            <span className="loading loading-spinner loading-sm"></span>
                                                        ) : (
                                                            <MdSave />
                                                        )}
                                                        Enviar Audio
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="flex flex-col gap-4">
                                        <div className="p-4 bg-success/10 rounded-lg border border-success/20">
                                            <p className="font-medium text-sm text-success">
                                                Audio ja enviado
                                            </p>
                                            {consultation.audioDuration && (
                                                <p className="text-xs text-base-content/60">
                                                    Duração:{' '}
                                                    {Math.floor(
                                                        consultation.audioDuration / 60
                                                    )}
                                                    min{' '}
                                                    {consultation.audioDuration % 60}s
                                                </p>
                                            )}
                                        </div>

                                        {consultation.transcriptionStatus ===
                                            'PENDING' &&
                                            !consultation.transcription && (
                                                <button
                                                    onClick={handleTranscribe}
                                                    disabled={transcribing}
                                                    className="btn btn-outline btn-secondary btn-sm gap-2"
                                                    title="Transcrever com IA (maior precisao)"
                                                >
                                                    {transcribing ? (
                                                        <span className="loading loading-spinner loading-sm"></span>
                                                    ) : (
                                                        <MdAutoAwesome />
                                                    )}
                                                    Transcrever com IA
                                                </button>
                                            )}

                                        {consultation.transcriptionStatus ===
                                            'PROCESSING' && (
                                                <div className="flex items-center gap-2 text-warning">
                                                    <span className="loading loading-spinner loading-sm"></span>
                                                    <span className="text-sm">
                                                        Transcrevendo com IA...
                                                    </span>
                                                </div>
                                            )}

                                        {consultation.transcriptionStatus ===
                                            'COMPLETED' && (
                                                <div className="flex items-center gap-2 text-success">
                                                    <MdCheck className="text-lg" />
                                                    <span className="text-sm">
                                                        Transcrição disponivel
                                                    </span>
                                                </div>
                                            )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                            <div className="card-body p-4">
                                <h2 className="card-title text-base mb-3">
                                    Ações Rápidas
                                </h2>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        onClick={() =>
                                            navigate(
                                                `/receituario/add?consultationId=${consultation.id}&clientId=${consultation.clientId}`
                                            )
                                        }
                                        className="btn btn-outline btn-sm gap-2"
                                    >
                                        <MdMedication />
                                        Receita
                                    </button>
                                    <button
                                        onClick={() =>
                                            navigate(
                                                `/core/reports/add?consultationId=${consultation.id}&clientId=${consultation.clientId}`
                                            )
                                        }
                                        className="btn btn-outline btn-sm gap-2"
                                    >
                                        <MdDescription />
                                        Relatório
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Transcription & AI */}
                    <div className="flex flex-col gap-4">
                        {/* Transcription */}
                        <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                            <div className="card-body p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h2 className="card-title text-base">
                                        Transcrição
                                    </h2>
                                    {isTranscribing && (
                                        <div className="flex items-center gap-2 text-xs text-secondary">
                                            <span className="inline-block w-2 h-2 bg-secondary rounded-full animate-pulse"></span>
                                            Transcrevendo...
                                        </div>
                                    )}
                                </div>

                                {/* Show real-time transcription while recording */}
                                {realTimeTranscription && !consultation.audioUrl ? (
                                    <div className="bg-base-200 rounded-lg p-4 max-h-[200px] overflow-y-auto">
                                        <p className="text-sm text-base-content whitespace-pre-wrap">
                                            {realTimeTranscription}
                                            {isTranscribing && (
                                                <span className="inline-block w-1 h-4 bg-secondary ml-1 animate-pulse"></span>
                                            )}
                                        </p>
                                    </div>
                                ) : (
                                    <TranscriptionViewer
                                        transcription={consultation.transcription || ''}
                                        status={consultation.transcriptionStatus}
                                        transcribedAt={consultation.transcribedAt}
                                        maxHeight="200px"
                                        showSearch={false}
                                    />
                                )}
                            </div>
                        </div>

                        {/* AI Summary */}
                        <div className="card bg-base-100 border border-base-300/50 shadow-sm flex-1">
                            <div className="card-body p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h2 className="card-title text-base">
                                        Resumo da IA
                                    </h2>
                                    {consultation.transcriptionStatus ===
                                        'COMPLETED' &&
                                        !consultation.aiSummary && (
                                            <button
                                                onClick={handleAnalyze}
                                                disabled={analyzing}
                                                className="btn btn-secondary btn-sm gap-1"
                                            >
                                                {analyzing ? (
                                                    <span className="loading loading-spinner loading-xs"></span>
                                                ) : (
                                                    <MdAutoAwesome />
                                                )}
                                                Gerar
                                            </button>
                                        )}
                                </div>
                                <AiSummaryCard
                                    summary={consultation.aiSummary}
                                    diagnosis={consultation.aiDiagnosis}
                                    recommendations={consultation.aiRecommendations}
                                    followUp={consultation.aiFollowUp}
                                    extractedData={consultation.aiExtractedData}
                                    clientId={consultation.clientId}
                                    consultationId={consultation.id}
                                    confidenceScore={consultation.aiConfidenceScore}
                                    isRegenerating={analyzing}
                                    className="bg-transparent border-0 shadow-none p-0"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmModal
                isOpen={showFinishModal}
                onClose={() => setShowFinishModal(false)}
                onConfirm={confirmFinishConsultation}
                title="Finalizar consulta"
                message="Deseja finalizar a consulta?"
                confirmButtonText="Finalizar"
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
